export const pricingData = {
  'lead-rescue-system': {
    name: 'Missed Call & Lead Rescue System',
    buildAndMaintain: {
      setup: 400,
      monthly: 150
    },
    buildAndBounce: {
      setup: 500
    },
    features: {
      'missed-call-textback': {
        name: 'Missed Call Text-Back',
        price: 0,
        monthlyPrice: 0,
        features: [
          'Automatic SMS to missed callers',
          'Includes booking link',
          'Friendly, professional tone'
        ]
      },
      'website-inquiry-reply': {
        name: 'Website Inquiry SMS Reply',
        price: 0,
        monthlyPrice: 0,
        features: [
          'Instant SMS to form submissions',
          'Includes booking link',
          'Lead captured in log'
        ]
      },
      'lead-log': {
        name: 'Simple Lead Log',
        price: 0,
        monthlyPrice: 0,
        features: [
          'Google Sheet or simple CRM',
          'Records all missed calls and inquiries',
          'Easy to review and follow up'
        ]
      },
      'gentle-followup': {
        name: 'Gentle Follow-Up',
        price: 100,
        monthlyPrice: 50,
        features: [
          'Follow-up for non-bookers',
          'Scheduled reminders',
          'Customizable messaging'
        ]
      }
    }
  },
  'chatbots-phone-agents': {
    name: 'AI Chatbots & Phone Agents',
    buildAndMaintain: {
      setup: 600,
      monthly: 250
    },
    buildAndBounce: {
      setup: 750
    },
    features: {
      'faq-handling': {
        name: 'FAQ Handling',
        price: 0,
        monthlyPrice: 0,
        features: [
          'Automated responses to common questions',
          'Custom knowledge base',
          'Natural conversation flow'
        ]
      },
      'lead-qualification': {
        name: 'Lead Qualification',
        price: 150,
        monthlyPrice: 50,
        features: [
          'Qualify leads before booking',
          'Collect relevant information',
          'Route to appropriate team member'
        ]
      },
      'appointment-booking': {
        name: 'Appointment Booking',
        price: 200,
        monthlyPrice: 100,
        features: [
          'Calendar integration',
          'Real-time availability',
          'Automated confirmations'
        ]
      }
    }
  },
  'website-chatbot': {
    name: 'Website Chatbot',
    buildAndMaintain: {
      setup: 500,
      monthly: 200
    },
    buildAndBounce: {
      setup: 600
    },
    features: {
      'appointment-booking': {
        name: 'AI Appointment Booking',
        price: 0,
        monthlyPrice: 0,
        features: [
          'Automated scheduling and calendar management',
          'Real-time availability updates',
          'Confirmation and reminder notifications'
        ]
      },
      'lead-capture': {
        name: 'Lead Capture',
        price: 100,
        monthlyPrice: 0,
        features: [
          'Contact information collection',
          'Prospect qualification',
          'Lead scoring and prioritization'
        ]
      },
      'customer-service': {
        name: 'Customer Service',
        price: 300,
        monthlyPrice: 250,
        features: [
          '24/7 customer support automation',
          'FAQ handling and resolution',
          'Ticket creation and routing'
        ]
      }
    }
  },
  'website-mobile': {
    name: 'Website & Mobile App',
    buildAndMaintain: {
      setup: 800,
      monthly: 550
    },
    buildAndBounce: {
      setup: 900
    },
    features: {
      'website': {
        name: 'Website',
        price: 0,
        monthlyPrice: 0,
        features: [
          'Custom design and branding',
          'Responsive layout',
          'Content management system'
        ]
      },
      'mobile-app': {
        name: 'Mobile App',
        price: 700,
        monthlyPrice: 200,
        features: [
          'iOS and Android compatibility',
          'Push notifications',
          'User authentication and profiles'
        ]
      }
    }
  },
  'phone-agent': {
    name: 'AI Phone Agent',
    buildAndMaintain: {
      setup: 500,
      monthly: 250
    },
    buildAndBounce: {
      setup: 650
    },
    features: {
      'appointment-booking': {
        name: 'Appointment Booking',
        price: 0,
        monthlyPrice: 0,
        features: [
          'Voice-based scheduling',
          'Calendar integration',
          'Automated confirmations'
        ]
      },
      'customer-service': {
        name: 'Customer Service',
        price: 300,
        monthlyPrice: 100,
        features: [
          'Natural language processing',
          'Call routing and transfer',
          'Issue resolution tracking'
        ]
      }
    }
  }
} as const;

export type ServiceId = keyof typeof pricingData;
export type BusinessModel = 'Build & Maintain' | 'Build & Bounce';
export type ServiceFeatureId = keyof (typeof pricingData)[ServiceId]['features'];

export interface SelectedFeatures {
  serviceId: ServiceId;
  model: BusinessModel;
  features: Set<ServiceFeatureId>;
}

export function calculateTotalCost(selected: SelectedFeatures) {
  const service = pricingData[selected.serviceId];
  const basePrice = selected.model === 'Build & Maintain' 
    ? service.buildAndMaintain.setup 
    : service.buildAndBounce.setup;

  const featuresCost = Array.from(selected.features).reduce((total, featureId) => {
    const feature = service.features[featureId];
    return total + feature.price;
  }, 0);

  const monthlyFeaturesCost = selected.model === 'Build & Maintain'
    ? Array.from(selected.features).reduce((total, featureId) => {
        const feature = service.features[featureId];
        return total + feature.monthlyPrice;
      }, service.buildAndMaintain.monthly)
    : 0;

  return {
    setup: basePrice + featuresCost,
    monthly: monthlyFeaturesCost
  };
}

export function calculateDiscount(setupCost: number, numberOfServices: number): {
  discountPercentage: number;
  discountAmount: number;
  finalCost: number;
} {
  let discountPercentage = 0;
  
  // Discount structure
  if (numberOfServices === 3) discountPercentage = 0.25;      // 25% for 3 services
  else if (numberOfServices === 2) discountPercentage = 0.15; // 15% for 2 services
  else discountPercentage = 0;                                // 0% for 1 service

  const discountAmount = setupCost * discountPercentage;
  const finalCost = setupCost - discountAmount;

  return {
    discountPercentage: discountPercentage * 100,
    discountAmount,
    finalCost
  };
}